import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileDown, Truck, ClipboardSignature, Trash2 } from "lucide-react";
import { DnItemsEditor } from "@/components/DnItemsEditor";

const STATUS = { Pending: "border-amber-500 text-amber-600", "Out for delivery": "border-blue-500 text-blue-600", Delivered: "border-green-600 text-green-700", Partial: "border-primary text-primary" };

export default function DeliveryNotes() {
  const { can } = useAuth();
  const [notes, setNotes] = useState([]);
  const [edit, setEdit] = useState(null);
  const [sp, setSp] = useSearchParams();
  const load = () => api.get("/delivery-notes").then(({ data }) => {
    setNotes(data);
    const openId = sp.get("edit");
    if (openId) { const n = data.find((x) => x.id === openId); if (n) setEdit(n); setSp({}); }
  }).catch(() => {});
  const remove = async (n) => {
    if (!window.confirm(`Delete ${n.number}? This cannot be undone.`)) return;
    try { await api.delete(`/delivery-notes/${n.id}`); toast.success(`${n.number} deleted`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader title="Delivery Notes" subtitle="Printable handover documents the client signs — created from invoices and accepted quotes" />
      <div className="bg-white border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-secondary hover:bg-secondary"><TableHead>Number</TableHead><TableHead>Ref</TableHead><TableHead>Customer</TableHead><TableHead>Delivery date</TableHead><TableHead>Items</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {notes.map((n) => (
              <TableRow key={n.id} data-testid={`dn-row-${n.number}`}>
                <TableCell className="font-mono text-sm">{n.number}</TableCell>
                <TableCell className="font-mono text-xs">{n.ref_number}</TableCell>
                <TableCell>{n.customer?.company_name}</TableCell>
                <TableCell className="text-sm">{fmtDate(n.delivery_date)}</TableCell>
                <TableCell className="text-sm tabular">{n.items?.length}</TableCell>
                <TableCell><Badge variant="outline" className={STATUS[n.status]}>{n.status}</Badge>{n.received_by && <div className="text-[10px] text-muted-foreground">Signed: {n.received_by}</div>}</TableCell>
                <TableCell className="text-right"><div className="flex justify-end gap-1">
                  <a href={`${API}/delivery-notes/${n.id}/pdf`} target="_blank" rel="noreferrer"><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`dn-pdf-${n.number}`}><FileDown className="h-4 w-4" /></Button></a>
                  {can("quotations") && <Button size="sm" variant="outline" className="h-8" onClick={() => setEdit(n)} data-testid={`dn-edit-${n.number}`}><ClipboardSignature className="h-3.5 w-3.5 mr-1" /> Update</Button>}
                  {can("quotations") && <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => remove(n)} title="Delete" data-testid={`dn-delete-${n.number}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                </div></TableCell>
              </TableRow>
            ))}
            {notes.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground"><Truck className="h-8 w-8 mx-auto mb-2 opacity-40" />No delivery notes yet. Open Quotations → actions on an accepted quote → "Create delivery note".</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      {edit && <EditDialog n={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function EditDialog({ n, onClose, onDone }) {
  const [f, setF] = useState({ delivery_date: n.delivery_date || "", notes: n.notes || "", received_by: n.received_by || "", status: n.status, items: n.items });
  const save = async (status) => {
    try { await api.patch(`/delivery-notes/${n.id}`, { ...f, status: status || f.status }); toast.success("Delivery note updated"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle className="font-display text-xl">{n.number} — {n.customer?.company_name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Delivery date</Label><Input type="date" value={f.delivery_date} onChange={(e) => setF({ ...f, delivery_date: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Received by (client name)</Label><Input value={f.received_by} onChange={(e) => setF({ ...f, received_by: e.target.value })} data-testid="dn-received-input" /></div>
          <div className="col-span-2 space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Items — edit products, ordered & delivered quantities</Label>
            <DnItemsEditor items={f.items} onChange={(items) => setF({ ...f, items })} showDelivered /></div>
          <div className="col-span-2 space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={() => save()} data-testid="dn-save-button">Save</Button>
          <Button variant="outline" onClick={() => save("Out for delivery")} data-testid="dn-out-button"><Truck className="h-4 w-4 mr-1.5" /> Out for delivery</Button>
          <Button onClick={() => save(f.items.some((it) => Number(it.delivered_qty) < Number(it.quantity)) ? "Partial" : "Delivered")} className="bg-green-700 hover:bg-green-800 text-white" data-testid="dn-delivered-button">Mark Delivered</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
